/**
 * ArenaFriendsService
 *
 * Manages global Arena lobby-level presence so friends can see each other
 * even when not yet seated at a table. Uses RTDB under `arena_lobby_presence/{uid}`.
 *
 * Distinct from per-table presence (rooms/{tableId}/presence/{uid}) which
 * is managed by ArenaPresenceService.
 */

import { rtdb } from '../../firebase';
import {
  ref,
  set,
  onValue,
  off,
  onDisconnect,
  serverTimestamp,
  update,
} from 'firebase/database';
import { membersApi } from '../../services/membersApi';

const LOBBY_PRESENCE_PATH = 'arena_lobby_presence';

export interface LobbyPresenceEntry {
  uid: string;
  displayName: string;
  photoURL: string;
  status: 'online' | 'away';
  tableId?: string | null;
  tableName?: string | null;
  lastSeen: number;
}

// Global server time offset for clock-skew compensation
let globalServerTimeOffset = 0;
let isOffsetListening = false;

export function ensureLobbyServerTimeOffsetListener(): void {
  if (isOffsetListening) return;
  isOffsetListening = true;
  try {
    const offsetRef = ref(rtdb, '.info/serverTimeOffset');
    onValue(offsetRef, (snap) => {
      const val = snap.val();
      if (typeof val === 'number') {
        globalServerTimeOffset = val;
      }
    });
  } catch {}
}

export function getEstimatedLobbyServerTime(): number {
  return Date.now() + globalServerTimeOffset;
}

// Module-level presence tracking state to prevent tearing down RTDB node on re-renders
let activePresenceUid: string | null = null;
let activeHeartbeat: any = null;
let activeConnectedRef: any = null;
let activeConnectedHandler: any = null;
let activeVisibilityHandler: any = null;
let activeDisplayName = 'Card Shark';
let activePhotoURL = '';

// Current table location tracked across heartbeats, reconnects, and profile updates
let currentActiveTableId: string | null = null;
let currentActiveTableName: string | null = null;

/**
 * Updates the user's current table location in their global presence node.
 * When tableId is null, user is considered browsing the lobby.
 */
export function updateLobbyPresenceLocation(
  uid: string,
  tableId: string | null,
  tableName?: string | null
): void {
  if (!uid) return;
  currentActiveTableId = tableId || null;
  currentActiveTableName = tableName || null;

  const presenceRef = ref(rtdb, `${LOBBY_PRESENCE_PATH}/${uid}`);
  update(presenceRef, {
    tableId: currentActiveTableId,
    tableName: currentActiveTableName,
    lastSeen: serverTimestamp(),
  }).catch(() => {});
}

/**
 * Updates the user's display name and photo without resetting the presence session.
 */
export function updateLobbyPresenceProfile(
  uid: string,
  displayName: string,
  photoURL: string
): void {
  if (!uid) return;
  activeDisplayName = displayName || activeDisplayName || 'Card Shark';
  activePhotoURL = photoURL || activePhotoURL || '';

  const presenceRef = ref(rtdb, `${LOBBY_PRESENCE_PATH}/${uid}`);
  update(presenceRef, {
    displayName: activeDisplayName,
    photoURL: activePhotoURL,
    lastSeen: serverTimestamp(),
  }).catch(() => {});
}

/**
 * Clears the user's presence from RTDB (used on sign-out).
 */
export function clearLobbyPresence(uid: string): void {
  if (!uid) return;
  if (activePresenceUid === uid) {
    activePresenceUid = null;
    if (activeHeartbeat) {
      clearInterval(activeHeartbeat);
      activeHeartbeat = null;
    }
    if (activeConnectedRef && activeConnectedHandler) {
      try {
        off(activeConnectedRef, 'value', activeConnectedHandler);
      } catch {}
    }
    if (typeof document !== 'undefined' && activeVisibilityHandler) {
      try {
        document.removeEventListener('visibilitychange', activeVisibilityHandler);
      } catch {}
    }
  }
  const presenceRef = ref(rtdb, `${LOBBY_PRESENCE_PATH}/${uid}`);
  try {
    onDisconnect(presenceRef).cancel().catch(() => {});
    set(presenceRef, null).catch(() => {});
  } catch {}
}

/**
 * Writes the current user own presence to `arena_lobby_presence/{uid}`.
 * Registers an onDisconnect handler so the entry is removed if the connection drops.
 * Handles tab visibility (online/away). Returns a teardown function.
 */
export function initLobbyPresence(
  uid: string,
  displayName: string,
  photoURL: string
): () => void {
  if (!uid) return () => {};

  ensureLobbyServerTimeOffsetListener();

  activeDisplayName = displayName || activeDisplayName || 'Card Shark';
  activePhotoURL = photoURL || activePhotoURL || '';

  const presenceRef = ref(rtdb, `${LOBBY_PRESENCE_PATH}/${uid}`);

  const getStatus = (): 'online' | 'away' =>
    typeof document !== 'undefined' && document.hidden ? 'away' : 'online';

  const writePresence = () => {
    const payload: Record<string, any> = {
      uid,
      displayName: activeDisplayName,
      photoURL: activePhotoURL,
      status: getStatus(),
      lastSeen: serverTimestamp(),
    };

    if (currentActiveTableId) {
      payload.tableId = currentActiveTableId;
      payload.tableName = currentActiveTableName || 'Table';
    }

    update(presenceRef, payload).catch(() => {});
  };

  // If already tracking this exact UID, update profile and ensure fresh timestamp
  if (activePresenceUid === uid && activeHeartbeat) {
    writePresence();
    return () => {
      try {
        onDisconnect(presenceRef).cancel().catch(() => {});
      } catch {}
    };
  }

  // If switching users, clean up previous
  if (activePresenceUid && activePresenceUid !== uid) {
    clearLobbyPresence(activePresenceUid);
  }

  activePresenceUid = uid;

  const connectedRef = ref(rtdb, '.info/connected');
  activeConnectedRef = connectedRef;

  const handleConnectionChange = (snap: any) => {
    if (activePresenceUid !== uid) return;
    if (snap.val()) {
      onDisconnect(presenceRef).remove().catch(() => {});
      writePresence();
    }
  };
  activeConnectedHandler = handleConnectionChange;

  const handleVisibilityChange = () => {
    if (activePresenceUid !== uid) return;
    update(presenceRef, {
      status: getStatus(),
      lastSeen: serverTimestamp(),
    }).catch(() => {});
  };
  activeVisibilityHandler = handleVisibilityChange;

  if (typeof navigator === 'undefined' || navigator.onLine) {
    writePresence();
  }

  onValue(connectedRef, handleConnectionChange);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  if (activeHeartbeat) clearInterval(activeHeartbeat);
  activeHeartbeat = setInterval(() => {
    if (activePresenceUid !== uid) return;
    const hbPayload: Record<string, any> = {
      lastSeen: serverTimestamp(),
    };
    if (currentActiveTableId) {
      hbPayload.tableId = currentActiveTableId;
      hbPayload.tableName = currentActiveTableName || 'Table';
    }
    update(presenceRef, hbPayload).catch(() => {});
  }, 300000);

  return () => {
    try {
      if (activeConnectedRef && activeConnectedHandler) {
        off(activeConnectedRef, 'value', activeConnectedHandler);
      }
      if (typeof document !== 'undefined' && activeVisibilityHandler) {
        document.removeEventListener('visibilitychange', activeVisibilityHandler);
      }
      if (activeHeartbeat) {
        clearInterval(activeHeartbeat);
        activeHeartbeat = null;
      }
      onDisconnect(presenceRef).cancel().catch(() => {});
    } catch {}
  };
}

/**
 * Subscribes to all users currently in the Arena lobby.
 * Entries older than 300s (with serverTimeOffset compensation) are considered stale and filtered out.
 * Includes periodic 15s re-evaluation to detect silent drop-offs.
 * Returns a teardown unsubscribe function.
 */
export function subscribeToLobbyPresence(
  callback: (users: LobbyPresenceEntry[]) => void
): () => void {
  ensureLobbyServerTimeOffsetListener();

  const lobbyRef = ref(rtdb, LOBBY_PRESENCE_PATH);
  let latestRawSnapshot: any = null;

  const parseAndDeliver = (raw: any) => {
    if (!raw || typeof raw !== 'object') {
      callback([]);
      return;
    }

    // 30 minutes margin prevents device clock skews or background timer throttling from falsely filtering active members
    const staleThresholdMs = 30 * 60 * 1000;
    const now = getEstimatedLobbyServerTime();
    const users: LobbyPresenceEntry[] = [];

    for (const [uid, val] of Object.entries(raw)) {
      if (!val || typeof val !== 'object') continue;
      const entry = val as any;

      const lastSeen = Number(entry.lastSeen) || 0;
      if (lastSeen > 0 && Math.abs(now - lastSeen) > staleThresholdMs) continue;

      users.push({
        uid,
        displayName: entry.displayName || 'Card Shark',
        photoURL: entry.photoURL || '',
        status: entry.status === 'away' ? 'away' : 'online',
        tableId: entry.tableId || null,
        tableName: entry.tableName || null,
        lastSeen,
      });
    }

    callback(users);
  };

  const handler = onValue(
    lobbyRef,
    (snap) => {
      latestRawSnapshot = snap.val();
      parseAndDeliver(latestRawSnapshot);
    },
    () => {
      callback([]);
    }
  );

  // Periodic re-evaluator ensures stale records are purged even if no new RTDB write occurs
  const staleInterval = setInterval(() => {
    if (latestRawSnapshot) {
      parseAndDeliver(latestRawSnapshot);
    }
  }, 300000);

  return () => {
    clearInterval(staleInterval);
    try {
      off(lobbyRef, 'value', handler);
    } catch {}
  };
}

export interface FollowingCacheEntry {
  followingIds: Set<string>;
  profileMap: Map<string, { displayName: string; photoURL: string }>;
}

const FOLLOWING_STORAGE_PREFIX = 'atl_friends_following_';
let cachedFollowUid: string | null = null;
let cachedFollowingEntry: FollowingCacheEntry | null = null;
let cachedFollowTimestamp = 0;
const FOLLOW_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

export function clearFollowingCache(): void {
  cachedFollowUid = null;
  cachedFollowingEntry = null;
  cachedFollowTimestamp = 0;
  if (typeof sessionStorage !== 'undefined') {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(FOLLOWING_STORAGE_PREFIX)) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {}
  }
}

export async function getCachedFollowing(
  uid: string,
  forceRefresh = false
): Promise<FollowingCacheEntry> {
  if (!uid) {
    return { followingIds: new Set(), profileMap: new Map() };
  }

  const now = Date.now();

  // 1. In-memory hot cache
  if (
    !forceRefresh &&
    cachedFollowUid === uid &&
    cachedFollowingEntry &&
    now - cachedFollowTimestamp < FOLLOW_CACHE_TTL_MS
  ) {
    return cachedFollowingEntry;
  }

  // 2. Persistent session storage cache for 0ms initial render
  if (!forceRefresh && typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(`${FOLLOWING_STORAGE_PREFIX}${uid}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.followingIds)) {
          const entry: FollowingCacheEntry = {
            followingIds: new Set(parsed.followingIds),
            profileMap: new Map(Object.entries(parsed.profileMap || {})),
          };
          cachedFollowUid = uid;
          cachedFollowingEntry = entry;
          cachedFollowTimestamp = parsed.timestamp || now;
          if (now - (parsed.timestamp || 0) < FOLLOW_CACHE_TTL_MS) {
            return entry;
          }
        }
      }
    } catch {}
  }

  const followSet = new Set<string>();
  const profileMap = new Map<string, { displayName: string; photoURL: string }>();

  try {
    const list = await membersApi.getFollowing(uid);
    if (Array.isArray(list)) {
      list.forEach((f: any) => {
        const id = f.id || f.uid;
        if (id) {
          followSet.add(id);
          profileMap.set(id, {
            displayName: f.displayName || f.nickname || f.firstName || 'Card Shark',
            photoURL: f.photoUrl || f.photoURL || f.avatarUrl || '',
          });
        }
      });
    }

    const entry: FollowingCacheEntry = { followingIds: followSet, profileMap };
    cachedFollowUid = uid;
    cachedFollowingEntry = entry;
    cachedFollowTimestamp = now;

    if (typeof sessionStorage !== 'undefined') {
      try {
        const serializable = {
          followingIds: Array.from(followSet),
          profileMap: Object.fromEntries(profileMap.entries()),
          timestamp: now,
        };
        sessionStorage.setItem(`${FOLLOWING_STORAGE_PREFIX}${uid}`, JSON.stringify(serializable));
      } catch {}
    }

    return entry;
  } catch (err) {
    console.warn('[ArenaFriendsService] getCachedFollowing error:', err);
    return cachedFollowingEntry || { followingIds: followSet, profileMap };
  }
}

